/**
 * Per-drone payload construction for the spray-record form.
 *
 * Extracted out of `SprayRecordFormComponent.onSubmit()` so it can be tested.
 * `jest.config.js` in this app is `testEnvironment: 'node'` with no
 * `jest-preset-angular`, so TestBed component specs are not runnable here —
 * a pure function is the only way this logic gets real coverage.
 *
 * ⚠️ The rule this file exists to protect (FAA §F.2.d spec, requirement 2):
 *
 *     absent  → "leave the stored value alone"
 *     null    → "clear it"
 *     n       → "store n"
 *
 * The Hub distinguishes those three by reading the RAW DTO with
 * `hasOwnProperty` before its model normalises with `?? null`. So a key that
 * is present-but-undefined is indistinguishable from an absent key on the
 * wire, and `numFlights: undefined` would mean "don't change it" — a user
 * clearing the input would silently do nothing.
 *
 * Hence `numFlights` is emitted EXPLICITLY and never through the
 * `flight.flightAltitude || undefined` pattern the neighbouring fields use.
 */
import { ChemicalUsage } from '../../../../core/data-models/chemical-usage.model';
import { FlightPathData } from '../../../../core/data-models/flight-path-data.model';

/** One per-drone entry as the form holds it. */
export interface DroneFlightFormData {
  id: string;
  droneId: string;
  droneSource: 'adn' | 'organization';
  pilotId: string;
  startTime: string;
  endTime: string;
  flightAltitude: number | null;
  flightSpeed: number | null;
  acreageSprayed: number | null;
  /** Takeoffs and landings by this aircraft at this location. Null = not determined. */
  numFlights: number | null;
  flightPath: FlightPathData | null;
  unitSystem: UnitSystem;
  chemicalUsage: ChemicalUsage[];
}

export type UnitSystem = 'metric' | 'imperial';

/** The drone fields snapshotted onto the flight row for compliance. */
export interface DroneSnapshotSource {
  name?: string;
  nNumber?: string;
  serialNumber?: string;
}

/** The pilot fields snapshotted onto the flight row for compliance. */
export interface PilotSnapshotSource {
  name?: string;
  part107Number?: string | null;
  part107Expiration?: string | null;
  stateApplicatorNumber?: string | null;
  stateApplicatorState?: string | null;
  stateApplicatorExpiration?: string | null;
}

/**
 * Build the wire payload for one per-drone entry.
 *
 * @param flight          the form's entry
 * @param drone           matching drone, for the immutable snapshot columns
 * @param pilot           matching pilot, for the immutable snapshot columns
 * @param applicationDate `YYYY-MM-DD`; the times are stored as
 *                        `timestamp without time zone`, so they are composed as
 *                        literal local strings and never run through `Date`,
 *                        which would shift them by the viewer's offset
 */
export function buildDroneFlightPayload(
  flight: DroneFlightFormData,
  drone: DroneSnapshotSource | undefined,
  pilot: PilotSnapshotSource | undefined,
  applicationDate: string
): Record<string, unknown> {
  const startTime = flight.startTime ? `${applicationDate}T${flight.startTime}:00` : undefined;
  const endTime = flight.endTime ? `${applicationDate}T${flight.endTime}:00` : undefined;
  const normalizedNumFlights = normalizeNumFlights(flight.numFlights);

  return {
    droneId: flight.droneId,
    droneSource: flight.droneSource,
    // Drone snapshot
    droneName: drone?.name || null,
    droneNNumber: drone?.nNumber || null,
    droneSerialNumber: drone?.serialNumber || null,
    // Pilot snapshot
    pilotId: flight.pilotId,
    pilotName: pilot?.name || null,
    pilotPart107Number: pilot?.part107Number || null,
    pilotPart107Expiration: pilot?.part107Expiration || null,
    pilotStateApplicatorNumber: pilot?.stateApplicatorNumber || null,
    pilotStateApplicatorState: pilot?.stateApplicatorState || null,
    pilotStateApplicatorExpiration: pilot?.stateApplicatorExpiration || null,
    // Flight timing
    startTime,
    endTime,
    flightAltitude: flight.flightAltitude || undefined,
    flightSpeed: flight.flightSpeed || undefined,
    acreageSprayed: flight.acreageSprayed,
    // ⚠️ Explicit. `?? null` and NOT `|| undefined`:
    //   - `|| undefined` would also swallow a legitimate 0
    //   - `undefined` reads as "absent" on the wire, i.e. "leave it alone",
    //     so clearing the input would silently fail to clear anything
    // Normalised once. Deriving the flag from a second call would let the two
    // fields disagree if the normalisation rule ever changes — and "a count
    // was sent but not marked manual" is exactly the state that would let a
    // future auto-derivation overwrite a pilot's number.
    numFlights: normalizedNumFlights,
    // Web is an explicit human-entry surface, so any value it sends was typed
    // by a person. Only ever sent as `true`; the Hub never writes it back to
    // false, so once true it stays true.
    numFlightsManuallySet: normalizedNumFlights !== null,
    flightPath: flight.flightPath,
    chemicalUsage: flight.chemicalUsage
  };
}

/**
 * Coerce whatever the numeric input produced into `number | null`.
 *
 * An `<input type="number">` bound through ngModel yields `null` when cleared,
 * but an empty string and `NaN` both turn up in practice depending on how the
 * value was set. All three mean "not determined" and must land on `null` —
 * never `undefined`, which the Hub would read as "leave the stored value
 * alone", and never `0`, which is a distinct fact meaning the aircraft flew
 * zero times.
 */
export function normalizeNumFlights(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}
